import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { RaptorLogo } from '@/components/RaptorLogo';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Check, KeyRound, Mail, ShieldCheck } from 'lucide-react';

type Step = 'request' | 'verify' | 'done';

export const ForgotPasswordPage: React.FC = () => {
  const nav = useNavigate();
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post('/auth/password-reset/start', { email: email.trim().toLowerCase() });
      toast.success('If your account exists, an OTP has been sent by SMS.');
      setStep('verify');
    } catch (err: unknown) {
      // Backend intentionally does not reveal existence — still advance so we
      // don't leak enumeration signal via UI either.
      toast.success('If your account exists, an OTP has been sent by SMS.');
      setStep('verify');
    } finally { setBusy(false); }
  };

  const verifyOtp = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!otp.trim() || !password || !confirm) return;
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/password-reset/verify', {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        new_password: password,
      });
      setStep('done');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Reset failed';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex-1 min-h-[calc(100vh-32px)] grid md:grid-cols-2">
      <div className="relative overflow-hidden hidden md:flex flex-col justify-between p-10 bg-white border-r border-raptor-neutral">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
          <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full raptor-gradient blur-3xl" />
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-raptor-cyan blur-3xl" />
        </div>
        <div className="relative z-10 inline-flex items-center justify-center rounded-2xl bg-white/60 backdrop-blur-sm border border-raptor-navy/5 shadow-[0_10px_30px_-15px_rgba(6,25,53,0.25)] px-6 py-4 self-start">
          <RaptorLogo size={64} slot="login_logo" />
        </div>
        <div className="relative z-10 max-w-md">
          <h2 className="font-headline text-raptor-navy text-4xl tracking-wide leading-tight">Reset your password securely.</h2>
          <p className="text-raptor-navy/70 font-body mt-4">We&apos;ll send a one-time code to the mobile number registered on your Raptor account. Enter the code and choose a new password to regain access.</p>
        </div>
        <div className="text-raptor-navy/50 text-xs font-body relative z-10">© Osprey UMA · Raptor Platform</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          {step === 'request' && (
            <>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Mail size={18} className="text-raptor-blue" /> Forgot password</CardTitle>
                <CardDescription>Enter the email or mobile number on your account and we&apos;ll send you a code by SMS.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={requestOtp} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email or mobile number</Label>
                    <Input id="email" data-testid="forgot-email" type="text" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" placeholder="you@company.com or 0821234567" />
                  </div>
                  <Button data-testid="forgot-request-btn" type="submit" variant="gradient" className="w-full" disabled={busy}>
                    {busy ? 'Sending…' : 'Send reset code'}
                  </Button>
                  <button type="button" onClick={() => nav('/login')} className="w-full text-xs text-raptor-navy/60 hover:text-raptor-blue inline-flex items-center justify-center gap-1">
                    <ArrowLeft size={12} /> Back to sign in
                  </button>
                </form>
              </CardContent>
            </>
          )}
          {step === 'verify' && (
            <>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><KeyRound size={18} className="text-raptor-blue" /> Enter code &amp; new password</CardTitle>
                <CardDescription>We&apos;ve sent an OTP to the phone number linked to <b>{email}</b>. It expires shortly — use it now.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={verifyOtp} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="otp">One-time code</Label>
                    <Input id="otp" data-testid="forgot-otp" required value={otp} onChange={(e) => setOtp(e.target.value)} inputMode="numeric" placeholder="6-digit code" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password">New password</Label>
                    <Input id="new-password" data-testid="forgot-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                    <p className="text-[11px] text-raptor-navy/50">Must meet the platform password policy (minimum length, mixed case, digits and symbols).</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password">Confirm new password</Label>
                    <Input id="confirm-password" data-testid="forgot-confirm" required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
                    {confirm && password !== confirm && (
                      <p className="text-[11px] text-red-600">Passwords do not match.</p>
                    )}
                  </div>
                  <Button data-testid="forgot-verify-btn" type="submit" variant="gradient" className="w-full" disabled={busy || password !== confirm}>
                    {busy ? 'Resetting…' : 'Reset password'}
                  </Button>
                  <button type="button" onClick={() => setStep('request')} className="w-full text-xs text-raptor-navy/60 hover:text-raptor-blue inline-flex items-center justify-center gap-1">
                    <ArrowLeft size={12} /> Use a different email or number
                  </button>
                </form>
              </CardContent>
            </>
          )}
          {step === 'done' && (
            <>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-600" /> Password reset</CardTitle>
                <CardDescription>Your password has been updated. You can now sign in with the new credentials.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-start gap-2">
                  <Check size={16} className="mt-0.5 flex-shrink-0" />
                  <div>All previous sessions for this account have been retained; you may sign in normally.</div>
                </div>
                <Button variant="gradient" className="w-full" onClick={() => nav('/login')} data-testid="forgot-back-to-login">
                  Back to sign in
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
